import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/components/seo/SEO";

const CARD_ROMANS = ["I", "II", "III", "IV"];

export default function Eventos() {
    const { t } = useTranslation();
    const cards = t("eventos.cards", { returnObjects: true });
    const types = t("eventos.form.types", { returnObjects: true });

    const [form, setForm] = useState({
        nombre: "",
        email: "",
        telefono: "",
        tipo: types[0] || "Boda",
        fecha: "",
        personas: "",
        mensaje: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.tipo) {
            toast.error(t("eventos.form.errorMissing"));
            return;
        }
        setSending(true);
        try {
            await api.post("/eventos", {
                ...form,
                personas: form.personas ? Number(form.personas) : null,
                fecha: form.fecha || null,
                email: form.email || null,
                telefono: form.telefono || null,
                mensaje: form.mensaje || null,
            });
            setSent(true);
            if (window.gtag) { window.gtag("event", "consulta_evento", { event_category: "conversion", event_label: "Consulta evento", value: 1 }); }
            toast.success(t("eventos.form.successToast"));
            setForm({ nombre: "", email: "", telefono: "", tipo: types[0] || "Boda", fecha: "", personas: "", mensaje: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <article className="pt-32 pb-24 paper-grain">
            <SEO
                title={t("eventos.seo.title")}
                description={t("eventos.seo.description")}
                keywords="bodas Ocaña, comuniones Ocaña, eventos empresa Ocaña, bautizos Ocaña, salones privados Toledo, celebraciones edificio histórico, bodas Toledo, eventos Aranjuez"
                path="/eventos"
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.eventos"), item: "/eventos" },
                ]}
            />
            {/* Hero */}
            <section className="max-w-5xl mx-auto px-6 text-center mb-32">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    className="roman-numeral"
                >
                    — {t("eventos.kicker")} —
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light text-[clamp(2.2rem,6vw,5.5rem)] leading-[0.95] text-ivory mt-8"
                >
                    {t("eventos.title1")}
                    <span className="block italic text-gold">{t("eventos.title2")}</span>
                    <span className="block">{t("eventos.title3")}</span>
                </motion.h1>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.8, duration: 1 }}
                    className="line-gold mx-auto my-10 origin-left"
                />
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1.2 }}
                    className="font-body text-lg text-stone max-w-2xl mx-auto leading-relaxed"
                >
                    {t("eventos.subtitle")}
                </motion.p>
            </section>

            {/* Cards */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-gold/15 mb-32">
                {cards.map((c, i) => (
                    <motion.div
                        key={c.t}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-30px" }}
                        transition={{ duration: 0.9, delay: i * 0.1 }}
                        className="bg-bg p-10 md:p-12 hover:bg-bg-warm transition-colors duration-700 group"
                    >
                        <p className="font-editorial italic text-xs tracking-[0.3em] uppercase text-gold/70">
                            N.º {CARD_ROMANS[i]}
                        </p>
                        <h3 className="font-display italic text-4xl text-ivory mt-4 group-hover:text-gold transition-colors duration-500">
                            {c.t}
                        </h3>
                        <p className="font-body text-stone leading-relaxed text-sm mt-6">
                            {c.d}
                        </p>
                    </motion.div>
                ))}
            </section>

            {/* Formulario */}
            <section className="max-w-3xl mx-auto px-6">
                <motion.header
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="text-center mb-14"
                >
                    <p className="roman-numeral">— {t("eventos.form.kicker")} —</p>
                    <h2 className="font-display text-4xl md:text-6xl text-ivory font-light mt-6">
                        {t("eventos.form.title1")}
                        <span className="italic text-gold"> {t("eventos.form.title2")}</span>
                    </h2>
                </motion.header>

                {sent ? (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="border border-gold/40 p-14 text-center"
                    >
                        <p className="font-display italic text-4xl text-gold">
                            {t("eventos.form.successTitle")}
                        </p>
                        <p className="font-body text-stone mt-6">
                            {t("eventos.form.successBody")}
                            <a href="tel:+34925484633" className="text-gold" data-cursor="hover">
                                925 48 46 33
                            </a>
                            .
                        </p>
                        <button
                            type="button"
                            onClick={() => setSent(false)}
                            className="btn-ghost mt-10"
                            data-cursor="hover"
                        >
                            {t("eventos.form.newConsult")}
                        </button>
                    </motion.div>
                ) : (
                    <form
                        onSubmit={submit}
                        data-testid="event-contact-form"
                        className="grid md:grid-cols-2 gap-x-10 gap-y-8"
                    >
                        <div className="md:col-span-2">
                            <label className="line-label">{t("eventos.form.name")} *</label>
                            <input
                                required
                                value={form.nombre}
                                onChange={handle("nombre")}
                                className="input-line"
                                data-testid="event-name"
                            />
                        </div>
                        <div>
                            <label className="line-label">{t("eventos.form.email")}</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={handle("email")}
                                className="input-line"
                                data-testid="event-email"
                            />
                        </div>
                        <div>
                            <label className="line-label">{t("eventos.form.phone")}</label>
                            <input
                                value={form.telefono}
                                onChange={handle("telefono")}
                                className="input-line"
                                data-testid="event-phone"
                            />
                        </div>
                        <div>
                            <label className="line-label">{t("eventos.form.type")} *</label>
                            <select
                                value={form.tipo}
                                onChange={handle("tipo")}
                                className="input-line bg-bg"
                                data-testid="event-type"
                            >
                                {types.map((tp) => (
                                    <option key={tp} value={tp}>
                                        {tp}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="line-label">{t("eventos.form.date")}</label>
                            <input
                                type="date"
                                value={form.fecha}
                                onChange={handle("fecha")}
                                className="input-line"
                                data-testid="event-date"
                            />
                        </div>
                        <div>
                            <label className="line-label">{t("eventos.form.people")}</label>
                            <input
                                type="number"
                                min="1"
                                value={form.personas}
                                onChange={handle("personas")}
                                className="input-line"
                                data-testid="event-people"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="line-label">{t("eventos.form.message")}</label>
                            <textarea
                                rows={4}
                                value={form.mensaje}
                                onChange={handle("mensaje")}
                                className="input-line resize-none"
                                data-testid="event-message"
                            />
                        </div>
                        <div className="md:col-span-2 text-center pt-4">
                            <button
                                type="submit"
                                disabled={sending}
                                className="btn-solid-alt disabled:opacity-50"
                                data-testid="event-submit-button"
                                data-cursor="hover"
                            >
                                {sending ? t("eventos.form.sending") : t("eventos.form.submit")}
                            </button>
                        </div>
                    </form>
                )}
            </section>
        </article>
    );
}
